import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import AddressMutationInput from "./AddressMutationInput/AddressMutationInput";
import { Props, State } from "./interfaces";
import { AddEditButton } from "./styles";
import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";

import { EmployeeAddressMutationSTO } from "../../../../models/employee-addresses/mutations";
import { EmployeeAddressMutationsApiService } from "../../../../services/employees/employees-address-mutations-api.service";
import { EmployeeCurrentAddressMutationsApiService } from "../../../../services/employees/employees-current-address-mutations-api.service";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";

import { EmployeeDataContext } from "../../EmployeeDataAndMutaions";

const AddEditAddressMutation = ({ original, mutation, hasMutation, handleReload }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.data_and_documents"; // tRP - translation root path

  const { employee, forManager } = useContext(EmployeeDataContext);

  const [state, setState] = useState<State>({
    original: null,
    mutation: null,
    resetMutation: null,
    isDialogOpen: false,
    isConfirmationChangesDialogOpen: false,
    loading: false,
    error: "",
  });

  useEffect(() => {
    if (!original || !mutation) return;

    setState((state) => ({
      ...state,
      original,
      mutation,
      resetMutation: mutation,
    }));
  }, [original, mutation]);

  const setIsDialogOpen = (value: boolean) => setState((state) => ({ ...state, isDialogOpen: value }));

  const setIsConfirmationChangesDialogOpen = (value: boolean) =>
    setState((state) => ({ ...state, isConfirmationChangesDialogOpen: value }));

  const closeMainDialog = () => {
    if (!isEqual(state.mutation, state.resetMutation)) {
      setIsConfirmationChangesDialogOpen(true);
    } else setIsDialogOpen(false);
  };

  const handleDiscardChanges = () =>
    setState((state) => ({
      ...state,
      mutation,
      resetMutation: mutation,
      isDialogOpen: false,
      isConfirmationChangesDialogOpen: false,
      error: "",
    }));

  const handleSave = async (): Promise<void> => {
    setState((state) => ({ ...state, loading: true }));

    try {
      const apiService = forManager
        ? new EmployeeAddressMutationsApiService()
        : new EmployeeCurrentAddressMutationsApiService();

      if (forManager) {
        apiService.useSaveItemsPath("/ApproveEmployeeAddressMutations");
      }

      const response = await apiService.saveItems([state.mutation as EmployeeAddressMutationSTO]);

      toastifyResponse(response, "all", "default_success_message", "failure_saving_data", "error_saving_data");

      setState((state) => ({
        ...state,
        loading: false,
        isDialogOpen: false,
      }));

      handleReload();
    } catch (error: any) {
      setState((state) => ({
        ...state,
        loading: false,
        error: error?.message ?? error,
      }));
    }
  };

  const handleChange = (field: string, value: string | boolean | number | Date) =>
    setState((state) => ({
      ...state,
      mutation: { ...(state.mutation as EmployeeAddressMutationSTO), [field]: value },
    }));

  return (
    <>
      <AddEditButton onClick={() => setIsDialogOpen(true)}>
        <>
          <FontAwesomeIcon icon={faChevronRight} /> &nbsp;
          <Typography variant={"subtitle2"}>
            {`${t(`${tRP}.common_keys.${hasMutation ? "edit_change_request" : "request_a_change"}`)}`}
          </Typography>
        </>
      </AddEditButton>
      <Dialog
        fullWidth
        open={state.isDialogOpen}
        onClose={closeMainDialog}
      >
        <DialogTitle
          title={`${employee?.name}: ${t(`${tRP}.employee_data.employee_address.edit_title`)}`}
          handleClose={closeMainDialog}
        />
        <DialogContent>
          <AddressMutationInput
            originalValue={state.original?.addressLine1}
            mutationValue={state.mutation?.addressLine1}
            handleChangeMutation={handleChange}
            field="addressLine1"
          />
          <AddressMutationInput
            originalValue={state.original?.addressLine2}
            mutationValue={state.mutation?.addressLine2}
            handleChangeMutation={handleChange}
            field="addressLine2"
          />
          <AddressMutationInput
            originalValue={state.original?.locality}
            mutationValue={state.mutation?.locality}
            handleChangeMutation={handleChange}
            field="locality"
          />
          <AddressMutationInput
            originalValue={state.original?.region}
            mutationValue={state.mutation?.region}
            handleChangeMutation={handleChange}
            field="region"
          />
          <AddressMutationInput
            originalValue={state.original?.zipCode}
            mutationValue={state.mutation?.zipCode}
            handleChangeMutation={handleChange}
            field="zipCode"
          />
          <AddressMutationInput
            originalValue={state.original?.toDate}
            mutationValue={state.mutation?.toDate}
            handleChangeMutation={handleChange}
            field="toDate"
          />
        </DialogContent>
        <DialogActions
          handleCancel={closeMainDialog}
          handleSave={handleSave}
          customSaveName={forManager ? `${t("buttons.approve")} & ${t("buttons.save")}` : `${t("buttons.save")}`}
          error={state.error}
          loading={state.loading}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.isConfirmationChangesDialogOpen}
        handleCancel={() => setIsConfirmationChangesDialogOpen(false)}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};

export default AddEditAddressMutation;
